import { reactive, watch } from 'vue';

const KEY = 'pds.printer';

export type PrinterTransport = 'network' | 'usb';
export type PaperWidth = '58mm' | '80mm';

export interface PrinterSettings {
  // L'agent d'impression tourne sur le PC branché à l'imprimante (m-scan-device-agent, port 4310 par défaut).
  agentUrl: string;
  transport: PrinterTransport;
  host: string;
  port: number;
  printerName: string;
  // Le nom affiché à l'écran : ce que le scan a trouvé, ou ce que l'opérateur a saisi.
  label: string;
  paperWidth: PaperWidth;
  // L'appareil de la liste auquel ce poste est connecté : gardé d'une visite à l'autre, vide si aucun.
  connectedDevice: string;
  // L'adresse d'agent qui a répondu pour cet appareil : la connexion ne vaut que tant que c'est toujours celle du poste.
  connectedAgent: string;
}

export const DEFAULT_PRINTER: PrinterSettings = {
  agentUrl: 'http://localhost:4310',
  transport: 'network',
  host: '',
  port: 9100,
  printerName: '',
  label: '',
  paperWidth: '80mm',
  connectedDevice: '',
  connectedAgent: '',
};

const read = (): PrinterSettings => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    // Seules les clés connues survivent : un réglage retiré du code ne traîne pas dans le navigateur.
    return Object.fromEntries(
      Object.entries(DEFAULT_PRINTER).map(([key, fallback]) => [
        key,
        saved[key] ?? fallback,
      ]),
    ) as unknown as PrinterSettings;
  } catch {
    return { ...DEFAULT_PRINTER };
  }
};

// Propre au poste : chaque tablette ou PC a sa propre imprimante, rien n'est envoyé au serveur.
const settings = reactive<PrinterSettings>(read());

watch(settings, (value) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Stockage refusé : le réglage vaut pour la session seulement.
  }
});

export const usePrinterSettings = () => settings;

// Une imprimante est choisie quand on sait comment la joindre.
export const isPrinterConfigured = (value: PrinterSettings) =>
  value.transport === 'network'
    ? value.host.trim() !== ''
    : value.printerName.trim() !== '';
